import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyC-qYJRXXRKqkgLBmbsKxmUngxMuliDro8",
  authDomain: "peer-2688f.firebaseapp.com",
  projectId: "peer-2688f",
  storageBucket: "peer-2688f.firebasestorage.app",
  messagingSenderId: "486784804829",
  appId: "1:486784804829:web:322c63e08b9c557c383834",
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);

// SHA-256 해시
export async function hashValue(value) {
  const encoder = new TextEncoder();
  const data = encoder.encode(value.toLowerCase().trim());
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// UUID 생성
function generateUUID() {
  return (
    "peer-" +
    ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (
        c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
      ).toString(16)
    )
  );
}

// 사원 인증 후 사용자 등록/복원
export async function registerOrRestoreUser(
  value,
  type,
  role,
  roleNum,
  hospital
) {
  const hash = await hashValue(value);
  const fieldName = type === "email" ? "emailHash" : "phoneHash";

  const q = query(collection(db, "users"), where(fieldName, "==", hash));
  const snap = await getDocs(q);

  if (!snap.empty) {
    const userData = snap.docs[0].data();
    return {
      uuid: userData.uuid,
      nick: userData.nick,
      hospital: userData.hospital || hospital,
      hasPassword: !!userData.passwordHash,
      isNew: false,
    };
  }

  const uuid = generateUUID();
  const nick = `${role} ${roleNum}`;
  const userData = {
    uuid,
    nick,
    [fieldName]: hash,
    role,
    hospital: hospital || "",
    createdAt: new Date(),
  };

  await setDoc(doc(db, "users", uuid), userData);
  return {
    uuid,
    nick,
    hospital: hospital || "",
    hasPassword: false,
    isNew: true,
  };
}

// 비밀번호 설정
export async function setPassword(uuid, password) {
  const passwordHash = await hashValue(password + uuid);
  await updateDoc(doc(db, "users", uuid), { passwordHash });
  return true;
}

// ID/PW 로그인 (이메일 또는 핸드폰)
export async function loginWithPassword(idValue, password) {
  let snap;
  if (idValue.includes("@")) {
    const hash = await hashValue(idValue);
    snap = await getDocs(
      query(collection(db, "users"), where("emailHash", "==", hash))
    );
  } else {
    const hash = await hashValue(idValue);
    snap = await getDocs(
      query(collection(db, "users"), where("phoneHash", "==", hash))
    );
  }

  if (snap.empty) throw new Error("USER_NOT_FOUND");

  const userData = snap.docs[0].data();
  if (!userData.passwordHash) throw new Error("NO_PASSWORD");

  const passwordHash = await hashValue(password + userData.uuid);
  if (passwordHash !== userData.passwordHash) throw new Error("WRONG_PASSWORD");

  return {
    uuid: userData.uuid,
    nick: userData.nick,
    hospital: userData.hospital || null,
  };
}

// 중복 추천 체크
export async function checkDuplicate(uuid, docId) {
  const q = query(
    collection(db, "recommendations"),
    where("uuid", "==", uuid),
    where("docId", "==", docId)
  );
  const snap = await getDocs(q);
  return !snap.empty;
}

// 추천 저장
export async function saveRec(
  docId,
  docName,
  docDept,
  docSpec,
  hid,
  uuid,
  nick,
  thumbs,
  tags,
  note,
  lastSearch
) {
  const isDuplicate = await checkDuplicate(uuid, docId);
  if (isDuplicate) throw new Error("DUPLICATE");

  await addDoc(collection(db, "recommendations"), {
    docId,
    docName,
    docDept,
    docSpec,
    hid,
    uuid,
    role: nick,
    thumbs,
    tags,
    note,
    lastSearch: lastSearch || "",
    createdAt: new Date(),
  });
}

// 검색어 저장
export async function logSearch(keyword, type = "keyword") {
  if (!keyword || keyword.trim().length < 2) return;
  await addDoc(collection(db, "searches"), {
    keyword: keyword.trim(),
    type,
    createdAt: new Date(),
  });
}

// 문의 저장
export async function saveMessage(uuid, nick, hospital, message, contact) {
  await addDoc(collection(db, "messages"), {
    uuid,
    nick,
    hospital,
    message: message.trim(),
    contact: contact || "",
    answer: "",
    createdAt: new Date(),
    answeredAt: null,
  });
}

// 내 문의 불러오기
export async function getMyMessages(uuid) {
  const q = query(collection(db, "messages"), where("uuid", "==", uuid));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// 서비스 신청
export async function registerWaitlist(hospitalName, email) {
  if (!hospitalName) return;
  const emailHash =
    email && email.includes("@") ? await hashValue(email) : null;
  await addDoc(collection(db, "waitlist"), {
    hospitalName: hospitalName.trim(),
    emailHash,
    createdAt: new Date(),
  });
}

// ─────────────────────────────────────────────
// 점심 메뉴 관련 함수
// ─────────────────────────────────────────────

// 오늘 날짜 문자열 (YYYY-MM-DD)
function getTodayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// 오늘의 메뉴 목록 가져오기 (해당 병원, 오늘 날짜)
export async function getTodayLunches(hospital) {
  const today = getTodayStr();
  try {
    const q = query(
      collection(db, "lunch_menus"),
      where("hospital", "==", hospital),
      where("date", "==", today)
    );
    const snap = await getDocs(q);
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    // 등록 시간 오름차순 (1호가 위로)
    list.sort((a, b) => {
      const ta = a.createdAt?.toMillis?.() || 0;
      const tb = b.createdAt?.toMillis?.() || 0;
      return ta - tb;
    });
    return list;
  } catch (e) {
    console.error("오늘 메뉴 조회 실패:", e);
    return [];
  }
}

// 이 병원의 식당 이름 목록 (드롭다운용, 자동 누적)
export async function getCafeteriaNames(hospital) {
  try {
    const q = query(
      collection(db, "lunch_menus"),
      where("hospital", "==", hospital)
    );
    const snap = await getDocs(q);
    const names = new Set();
    snap.docs.forEach((d) => {
      const c = d.data().cafeteria;
      if (c && c.trim()) names.add(c.trim());
    });
    return Array.from(names).sort();
  } catch (e) {
    console.error("식당 목록 조회 실패:", e);
    return [];
  }
}

// 점심 메뉴 등록
export async function saveLunchMenu(hospital, cafeteria, menu, uuid, nick) {
  const today = getTodayStr();
  // 오늘 이 병원 첫 등록인지 확인
  const q = query(
    collection(db, "lunch_menus"),
    where("hospital", "==", hospital),
    where("date", "==", today)
  );
  const snap = await getDocs(q);
  const isFirst = snap.empty;
  // 저장
  const docRef = await addDoc(collection(db, "lunch_menus"), {
    hospital,
    date: today,
    cafeteria: (cafeteria || "").trim(),
    menu: menu.trim(),
    uploaderUuid: uuid,
    uploaderNick: nick,
    isFirstOfDay: isFirst,
    likes: 0,
    soso: 0,
    likedBy: [],
    sosoBy: [],
    createdAt: serverTimestamp(),
  });
  return { id: docRef.id, isFirst };
}

// 좋아요 / 그저그래요 (중복 방지, 토글 가능)
export async function reactToLunch(lunchId, uuid, reaction) {
  const ref = doc(db, "lunch_menus", lunchId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data();
  const likedBy = data.likedBy || [];
  const sosoBy = data.sosoBy || [];

  // 이미 같은 반응을 했으면 → 취소
  if (reaction === "like" && likedBy.includes(uuid)) {
    await updateDoc(ref, {
      likes: Math.max(0, (data.likes || 0) - 1),
      likedBy: likedBy.filter((u) => u !== uuid),
    });
    return;
  }
  if (reaction === "soso" && sosoBy.includes(uuid)) {
    await updateDoc(ref, {
      soso: Math.max(0, (data.soso || 0) - 1),
      sosoBy: sosoBy.filter((u) => u !== uuid),
    });
    return;
  }

  // 다른 반응으로 바꾸기 (like ↔ soso)
  const updates = {};
  if (reaction === "like") {
    updates.likes = (data.likes || 0) + 1;
    updates.likedBy = [...likedBy, uuid];
    if (sosoBy.includes(uuid)) {
      updates.soso = Math.max(0, (data.soso || 0) - 1);
      updates.sosoBy = sosoBy.filter((u) => u !== uuid);
    }
  } else {
    updates.soso = (data.soso || 0) + 1;
    updates.sosoBy = [...sosoBy, uuid];
    if (likedBy.includes(uuid)) {
      updates.likes = Math.max(0, (data.likes || 0) - 1);
      updates.likedBy = likedBy.filter((u) => u !== uuid);
    }
  }
  await updateDoc(ref, updates);
}

// ─────────────────────────────────────────────
// 이번 달 베스트 메뉴
// ─────────────────────────────────────────────

// 이번 달 시작/끝 날짜 문자열 (YYYY-MM-DD)
function getMonthRange() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const start = `${y}-${m}-01`;
  const lastDay = new Date(y, d.getMonth() + 1, 0).getDate();
  const end = `${y}-${m}-${String(lastDay).padStart(2, "0")}`;
  return { start, end, year: y, month: d.getMonth() + 1 };
}

// 이 병원의 이번 달 베스트 메뉴 (좋아요 1개 이상 중 최다)
export async function getMonthlyBest(hospital) {
  const { start, end } = getMonthRange();
  try {
    // 인덱스 안 쓰는 단순 쿼리 (hospital만 필터)
    const q = query(
      collection(db, "lunch_menus"),
      where("hospital", "==", hospital)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    // 자바스크립트에서 날짜 + 좋아요 필터링
    let best = null;
    snap.docs.forEach((d) => {
      const data = d.data();
      // 이번 달 데이터만
      if (!data.date || data.date < start || data.date > end) return;
      // 좋아요 1개 이상 + 최다
      if ((data.likes || 0) >= 1) {
        if (!best || (data.likes || 0) > (best.likes || 0)) {
          best = { id: d.id, ...data };
        }
      }
    });
    return best;
  } catch (e) {
    console.error("이번 달 베스트 조회 실패:", e);
    return null;
  }
}

// 5개 병원 모두의 이번 달 베스트 메뉴
export async function getAllHospitalsBest() {
  const hospitals = ["amc", "snuh", "smc", "yuhs", "cmcseoul"];
  const results = await Promise.all(
    hospitals.map(async (hid) => {
      const best = await getMonthlyBest(hid);
      return { hospital: hid, best };
    })
  );
  return results;
}

// ─────────────────────────────────────────────
// 동료 정보 공유
// ─────────────────────────────────────────────

// 동료 정보 공유 저장
export async function saveDoctorChangeRequest(data) {
  await addDoc(collection(db, "doctor_change_requests"), {
    type: data.type,           // "add" | "remove" | "update"
    hospital: data.hospital,
    docName: data.docName || "",
    docDept: data.docDept || "",
    docSpec: data.docSpec || "",
    reason: data.reason || "",
    requesterUuid: data.requesterUuid || "anonymous",
    requesterNick: data.requesterNick || "익명",
    status: "pending",
    createdAt: new Date(),
  });
}